import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';
interface FaqItem {
  q: string;
  a: string;
}
interface FaqCategory {
  cat: string;
  icon: React.ReactNode;
  open?: boolean;
  items: FaqItem[];
}
const FAQS: FaqCategory[] = [
  {
    cat: 'CREDITS & PAYMENTS',
    open: true,
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="2" y="6" width="20" height="13" rx="2" />
        <path d="M2 10h20" />
      </svg>
    ),
    items: [
      {
        q: 'Do my credits expire?',
        a: 'No. Credits sit in your account until you are ready to use them. There is no expiry date and no monthly charge. Buy when it suits you, use when you want.'
      },
      {
        q: 'When does my card get charged?',
        a: 'Your card is charged when you purchase a credit bundle — not when you place an order. At checkout you spend credits, not money. Your saved card is only used separately if a late fee, damage fee, or non-return charge becomes due.'
      },
      {
        q: 'Is there a minimum order?',
        a: 'Yes. A minimum spend of 3 credits is required per checkout. The only exception is if you have credits that were previously refunded to your account following an accepted fit return — those refunded credits can be used without meeting the 3-credit minimum, though a £4.99 delivery fee will apply.'
      }
    ]
  },
  {
    cat: 'ORDERS & DELIVERY',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="1" y="7" width="15" height="10" rx="1" />
        <path d="M16 11h4l3 3v3h-7" />
        <circle cx="6" cy="19" r="2" />
        <circle cx="19" cy="19" r="2" />
      </svg>
    ),
    items: [
      {
        q: 'Are the items clean when they arrive?',
        a: 'Yes, always. Every item is professionally cleaned and quality checked before it is packed and dispatched to you. You will receive your item in fresh, wearable condition every time.'
      },
      {
        q: 'Where do you deliver?',
        a: 'We currently deliver across the UK. Delivery is tracked and you will receive updates by email.'
      }
    ]
  },
  {
    cat: 'FIT & RETURNS',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M3 6l3-3 3 3M3 6v12l3 3 3-3M3 6h18M21 6l-3-3-3 3M21 6v12l-3 3-3-3" />
      </svg>
    ),
    items: [
      {
        q: 'What if something does not fit?',
        a: 'Contact us within 48 hours of delivery. If the item is unworn other than trying on, you can return it to us at your own cost and we will refund the credits used on that item to your account. A £4.99 delivery fee will apply when refunded credits are used on your next order.'
      },
      {
        q: 'Can I send it back before 30 days?',
        a: 'Absolutely. You are welcome to return an item early. If you need a return label sooner than Day 25, just contact us at team@labelrotation.co.uk and we will email one to you straight away.'
      },
      {
        q: 'What bag should I use to return?',
        a: 'Any suitable mailing bag works. Something padded or robust enough to protect the item in transit is ideal. Make sure it is sealed securely before posting.'
      },
      {
        q: 'How does the return label work?',
        a: 'We email you a prepaid return label between Day 25 and Day 30. Print it, attach it to your parcel, and drop it at any collection point for our carrier. The cost of return postage is covered by us — there is nothing to pay.'
      }
    ]
  },
  {
    cat: 'ROTATION & DEADLINES',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
    ),
    items: [
      {
        q: 'When does my 30 days start?',
        a: 'Your 30-day rotation begins on the date your delivery is confirmed by the courier.'
      },
      {
        q: 'What if I am late returning?',
        a: 'A 2-day grace period applies after Day 30. After Day 32, a late fee of £10 per day applies until the item is in transit back to us.'
      }
    ]
  },
  {
    cat: 'DAMAGE & LOSS',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M12 2l9 4v6c0 5-4 9-9 10-5-1-9-5-9-10V6l9-4z" />
      </svg>
    ),
    items: [
      {
        q: 'What counts as damage?',
        a: 'Normal wear is expected and never charged. A damage fee only applies if an item cannot be returned to the rotation — things like permanent stains, tears, burns, or structural damage.'
      },
      {
        q: 'What if an item is lost?',
        a: 'If an item is lost or cannot be returned, the replacement value will be charged to your saved payment method. We will always notify you at least 48 hours before applying any charge.'
      }
    ]
  }
];
// Default icon used for any FAQ category coming from the admin CMS —
// the admin form doesn't let you pick an icon, so a neutral one is used
// for everything except the categories that match the hardcoded ones.
const DEFAULT_FAQ_ICON = (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M10 9h4" />
    <path d="M12 12v4" />
    <path d="M12 18h0" />
  </svg>
);
export function HowItWorks() {
  // The How It Works page uses a bespoke layout — no docx-override path here.
  // (Legal/policy pages still support the docx upload via /admin/site-content.)
  const [openCats, setOpenCats] = useState<Record<number, boolean>>({ 0: true });
  const [adminFaqs, setAdminFaqs] = useState<FaqCategory[] | null>(null);

  useEffect(() => {
    // Pull admin-managed FAQ items from site_content. The admin form
    // stores them under faq_items as { items: [{ question, answer, category }] }.
    // We group by category (uppercased) so the existing accordion render
    // doesn't need any changes — and pair up category names with the
    // hardcoded icons where possible so the visual stays consistent.
    apiFetch<Record<string, unknown>>('/api/catalog/site-content')
      .then((all) => {
        const raw = (all.faq_items as
          | {
              items?: Array<{
                question: string;
                answer: string;
                category?: string;
              }>;
            }
          | undefined)?.items;
        if (!raw || raw.length === 0) return;

        const iconByCat = new Map<string, React.ReactNode>(
          FAQS.map((f) => [f.cat.toUpperCase(), f.icon])
        );

        const byCat = new Map<string, FaqItem[]>();
        for (const it of raw) {
          const cat = (it.category?.trim() || 'GENERAL').toUpperCase();
          if (!byCat.has(cat)) byCat.set(cat, []);
          byCat.get(cat)!.push({ q: it.question, a: it.answer });
        }

        setAdminFaqs(
          [...byCat.entries()].map(([cat, items]) => ({
            cat,
            icon: iconByCat.get(cat) ?? DEFAULT_FAQ_ICON,
            items
          }))
        );
      })
      .catch(() => {
        // Network blip — fall through to the hardcoded fallback.
      });
  }, []);

  // Admin-saved FAQs replace the hardcoded fallback when present.
  const faqList = adminFaqs ?? FAQS;

  return (
    <section className="screen">
      <div className="scroll hiw-scroll">
        {/* HERO */}
        <div className="hiw-hero">
          <div className="hiw-eyebrow">Simple from start to finish</div>
          <h1>HOW IT WORKS</h1>
          <p>
            Borrow premium pieces, wear them for 30 days, send them back. No
            subscriptions, no commitment.
          </p>
        </div>
        {/* STEPS */}
        <section className="hiw-steps-section">
          <div className="hiw-steps-grid">
            <StepCard
              num="01"
              title="PICK YOUR PIECES"
              desc="Browse our curated rotation and choose the pieces you want. Filter by category, size, or credit value."
              icon={
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
                  <circle cx="11" cy="11" r="7" />
                  <path d="M21 21l-4.3-4.3" />
                </svg>
              }
            />
            <StepCard
              num="02"
              title="BUY CREDITS"
              desc="Top up your account with credits. They never expire — use them whenever you want. No monthly charges."
              icon={
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
                  <circle cx="9" cy="9" r="6" />
                  <circle cx="15" cy="15" r="6" />
                </svg>
              }
            />
            <StepCard
              num="03"
              title="WEAR FOR 30 DAYS"
              desc="Your 30-day rotation begins on the date of confirmed delivery. Enjoy your pieces."
              icon={
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
                  <rect x="3" y="5" width="18" height="16" rx="2" />
                  <path d="M3 9h18M8 3v4M16 3v4" />
                </svg>
              }
            />
            <StepCard
              num="04"
              title="SEND THEM BACK"
              desc="We email you a prepaid return label between Day 25–30. Pack it up and drop it at any collection point. Return postage is on us."
              icon={
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 16V8l-9-5-9 5v8l9 5 9-5z" />
                  <path d="M3.3 7l8.7 5 8.7-5" />
                  <path d="M12 22V12" />
                </svg>
              }
            />
            <StepCard
              num="05"
              title="REFRESH"
              desc="Return your items and choose something new. Keep your wardrobe moving without buying a thing."
              icon={
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
                  <path d="M21 12a9 9 0 11-3-6.7" />
                  <path d="M21 4v5h-5" />
                </svg>
              }
            />
          </div>
          {/* TIMELINE */}
          <div className="hiw-section-label">Your rotation, mapped out</div>
          <div className="hiw-section-title">THE TIMELINE</div>
          <table className="timeline-table">
            <thead>
              <tr>
                <th></th>
                <th>DAY</th>
                <th>EVENT</th>
              </tr>
            </thead>
            <tbody>
              <TimelineRow
                day="Day 1"
                title="Delivery confirmed"
                desc="Your 30-day rotation period begins."
                icon={
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M21 16V8l-9-5-9 5v8l9 5 9-5z" />
                  </svg>
                }
              />
              <TimelineRow
                day="Days 25–30"
                title="Return label emailed to you"
                desc="Along with reminders so you never miss it."
                icon={
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="3" y="5" width="18" height="14" rx="2" />
                    <path d="M3 7l9 6 9-6" />
                  </svg>
                }
              />
              <TimelineRow
                day="Day 30"
                title="Return due"
                desc="Your item must be posted back by this date."
                icon={
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="3" y="5" width="18" height="16" rx="2" />
                    <path d="M3 9h18M8 3v4M16 3v4" />
                  </svg>
                }
              />
              <TimelineRow
                day="Day 32"
                title="Grace period ends"
                desc="Last day to post your return without incurring a late fee."
                icon={
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 7v5l3 2" />
                  </svg>
                }
              />
              <TimelineRow
                day="Day 33+"
                title="£10 per day late fee applies"
                desc="Stops the moment your item is in transit back to us."
                warn
                icon={
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M12 2 1 22h22L12 2z" />
                    <path d="M12 9v6M12 18h0" />
                  </svg>
                }
              />
              <TimelineRow
                day="Day 40+"
                title="Non-return charge applies"
                desc="Replacement value charged to your saved card."
                warn
                icon={
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="2" y="6" width="20" height="13" rx="2" />
                    <path d="M2 10h20" />
                  </svg>
                }
              />
            </tbody>
          </table>
        </section>
        {/* FAQ */}
        <section className="hiw-faq-section">
          <div className="hiw-section-label" style={{ textAlign: 'center' }}>
            Everything you need to know
          </div>
          <div className="hiw-section-title" style={{ textAlign: 'center' }}>
            COMMON QUESTIONS
          </div>
          {faqList.map((cat, i) => {
            const open = !!openCats[i];
            return (
              <div key={cat.cat} className="faq-group">
                <button
                  className={`faq-cat-btn${open ? ' open' : ''}`}
                  onClick={() => setOpenCats((s) => ({ ...s, [i]: !s[i] }))}
                  type="button"
                >
                  <span className="l">
                    {cat.icon} {cat.cat}
                  </span>
                  <span className="chev">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </button>
                <div className={`faq-body${open ? ' open' : ''}`}>
                  <div style={{ height: 4 }} />
                  {cat.items.map((it) => (
                    <div key={it.q} className="faq-item">
                      <div className="faq-q">{it.q}</div>
                      <div className="faq-a">{it.a}</div>
                    </div>
                  ))}
                  <div style={{ height: 4 }} />
                </div>
              </div>
            );
          })}
        </section>
        {/* HELP BAR */}
        <div className="hiw-help-bar">
          <div>
            <div className="t">STILL NEED HELP?</div>
            <a className="em" href="mailto:team@labelrotation.co.uk">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="M3 7l9 6 9-6" />
              </svg>
              team@labelrotation.co.uk
            </a>
          </div>
          <Link to="/browse" className="hiw-browse-btn">
            BROWSE PIECES
          </Link>
        </div>
        {/* CTA FOOTER */}
        <div className="hiw-cta-footer">
          <h2>
            READY TO
            <br />
            ROTATE?
          </h2>
          <p>Browse the rotation and pick your first pieces.</p>
          <Link to="/browse" className="btn">
            BROWSE PIECES
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
}
function StepCard({
  num,
  title,
  desc,
  icon
}: {
  num: string;
  title: string;
  desc: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="step-card">
      <div className="step-num">{num}</div>
      <div className="step-icon">{icon}</div>
      <div className="step-title">{title}</div>
      <div className="step-desc">{desc}</div>
    </div>
  );
}
function TimelineRow({
  day,
  title,
  desc,
  icon,
  warn
}: {
  day: string;
  title: string;
  desc: string;
  icon: React.ReactNode;
  warn?: boolean;
}) {
  return (
    <tr>
      <td className="tl-icon">{icon}</td>
      <td className={`day-cell${warn ? ' warn' : ''}`}>{day}</td>
      <td>
        <div className="event-title" style={warn ? { color: 'var(--warn)' } : undefined}>
          {title}
        </div>
        <div className="event-desc">{desc}</div>
      </td>
    </tr>
  );
}
